import { Button, QRCode, Tooltip, Typography } from 'antd';
import { Apple, Smartphone } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { pickLocalized } from '../i18n/translate.js';
import { FancyModal } from './ui/FancyModal.js';

// 下载链接可经环境变量覆盖；默认 APK 落在前端 public/downloads/ 下（nginx 从 dist 根目录提供）。
// iOS 不能直接下载安装包，必须走 App Store / TestFlight，未配置时显示“即将上架”。
const ANDROID_URL =
  (import.meta.env.VITE_APP_DOWNLOAD_ANDROID_URL as string | undefined) ||
  '/downloads/crest-android.apk';
const IOS_URL = (import.meta.env.VITE_APP_DOWNLOAD_IOS_URL as string | undefined) || '';

const BRAND = '#185FA5';

function toAbsolute(url: string): string {
  if (!url) return '';
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
}

const tt = (zh: string, en: string) => pickLocalized(zh, en);

/** 顶栏右上角“下载手机 App”入口：弹出 Android / iOS 二维码 + 下载选择 */
export function AppDownloadButton({ label }: { label?: ReactNode } = {}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title={tt('下载手机 App', 'Get the mobile app')}>
        <Button
          type="text"
          aria-label={tt('下载手机 App', 'Get the mobile app')}
          className="crest-header-action"
          icon={<Smartphone size={16} />}
          onClick={() => setOpen(true)}
        >
          {label}
        </Button>
      </Tooltip>
      <FancyModal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={440}
        icon={<Smartphone size={18} />}
        title={tt('下载手机 App', 'Download the mobile app')}
        subtitle={tt(
          '扫码或点击下载，支持 Android 与 iOS',
          'Scan or click to download — Android & iOS',
        )}
      >
        <div style={{ display: 'flex', gap: 16, paddingTop: 4 }}>
          <PlatformCard
            label="Android"
            icon={<Smartphone size={18} />}
            href={ANDROID_URL}
            qr={toAbsolute(ANDROID_URL)}
            cta={tt('下载 APK', 'Download APK')}
            note={tt('扫码或点此下载安装包', 'Scan or tap to get the APK')}
            download
          />
          <PlatformCard
            label="iOS"
            icon={<Apple size={18} />}
            href={IOS_URL}
            qr={toAbsolute(IOS_URL)}
            cta={IOS_URL ? 'App Store' : tt('即将上架', 'Coming soon')}
            note={
              IOS_URL
                ? tt('App Store / TestFlight 安装', 'Install via App Store / TestFlight')
                : tt('iOS 版即将上架', 'iOS version coming soon')
            }
          />
        </div>
      </FancyModal>
    </>
  );
}

function PlatformCard({
  label,
  icon,
  href,
  qr,
  cta,
  note,
  download,
}: {
  label: string;
  icon: ReactNode;
  href: string;
  qr: string;
  cta: string;
  note: string;
  download?: boolean;
}) {
  const enabled = !!href;
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: BRAND }}>
        {icon}
        <Typography.Text strong>{label}</Typography.Text>
      </div>
      {qr ? (
        <QRCode value={qr} size={116} bordered={false} />
      ) : (
        <div style={qrPlaceholder}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {tt('暂无', 'N/A')}
          </Typography.Text>
        </div>
      )}
      <Typography.Text type="secondary" style={{ fontSize: 11, textAlign: 'center' }}>
        {note}
      </Typography.Text>
      {enabled ? (
        <a
          href={href}
          {...(download ? { download: '' } : { target: '_blank', rel: 'noreferrer' })}
          style={ctaStyle}
        >
          {cta}
        </a>
      ) : (
        <span style={{ ...ctaStyle, background: '#E6E8EB', color: '#9AA1AC', cursor: 'default' }}>
          {cta}
        </span>
      )}
    </div>
  );
}

const cardStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  gap: 8,
  padding: '14px 12px',
  borderRadius: 12,
  border: '0.5px solid #E6E8EB',
};

const qrPlaceholder = {
  width: 116,
  height: 116,
  borderRadius: 8,
  background: '#F1F3F5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const ctaStyle = {
  width: '100%',
  textAlign: 'center' as const,
  padding: '8px 0',
  borderRadius: 8,
  background: BRAND,
  color: '#fff',
  fontSize: 13,
  fontWeight: 500,
  textDecoration: 'none',
};
